import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export type Period = 'this_month' | 'last_month' | 'this_year' | 'all_time'

/**
 * Org-scoping value for admin endpoints.
 * - undefined → omit; backend defaults to the caller's active org.
 * - 'all'     → cross-org aggregate (superadmin-only). Accepted ONLY by the
 *               aggregate endpoints (billing summary / by-org / rd-spend);
 *               row-level endpoints such as the ledger reject it with a 400.
 * - any other string → that specific org id (drill-down).
 */
export type OrgScope = string | undefined

export interface BillingTotals {
  period: Period
  from_ts: number
  to_ts: number
  org_id: string | null
  cost_usd: number
  price_usd: number
  profit_usd: number
  margin_pct: number | null
  minutes: number
  call_count: number
  breakdown: Record<string, {
    cost_usd: number
    price_usd: number
    minutes: number
    call_count: number
  }>
  // v6: storage cost+price folded into the totals so margin reflects
  // both per-minute and storage. Sub-line breakdown is also surfaced
  // for the Overview tab cards.
  minutes_cost_usd: number
  minutes_price_usd: number
  storage_cost_usd: number
  storage_price_usd: number
  storage_gb: number
}

export interface OrgBillingRow {
  org_id: string
  org_name: string | null
  org_slug: string | null
  cost_usd: number
  price_usd: number
  profit_usd: number
  margin_pct: number | null
  minutes: number
  call_count: number
  minutes_cost_usd: number
  minutes_price_usd: number
  storage_cost_usd: number
  storage_price_usd: number
  storage_gb: number
}

export interface LedgerRow {
  id: string
  org_id: string
  user_id: string | null
  call_id: string | null
  operation_id: string
  event_type: string
  cost_usd: number
  price_usd: number | null
  profit_usd: number
  provider: string
  billable: number
  minutes: number | null
  source: string
  created_at: number
}

export interface RdSpend {
  period: Period
  from_ts: number
  to_ts: number
  cost_usd: number
  minutes: number
  call_count: number
}

export function useBillingSummary(period: Period, orgScope?: OrgScope) {
  const qs = new URLSearchParams({ period })
  if (orgScope) qs.set('org_id', orgScope)
  return useQuery({
    queryKey: ['admin', 'billing', 'summary', period, orgScope ?? null],
    queryFn: () => apiFetch<BillingTotals>(`/api/admin/billing/summary?${qs.toString()}`),
  })
}

export function useBillingByOrg(period: Period) {
  // billing/orgs is intrinsically cross-org — used to populate the per-org
  // ranking table when the user is in 'all' scope. No scope parameter.
  return useQuery({
    queryKey: ['admin', 'billing', 'orgs', period],
    queryFn: () =>
      apiFetch<{ period: Period; from_ts: number; to_ts: number; orgs: OrgBillingRow[] }>(
        `/api/admin/billing/orgs?period=${period}`,
      ),
  })
}

/**
 * Row-level usage ledger. NOTE: `orgScope` must NOT be `'all'` here — the
 * backend rejects the cross-org sentinel on this endpoint with a 400, because
 * each row carries `call_id` / `org_id` / `user_id` and an unfiltered read is
 * a cross-tenant identifier dump. Use `useBillingTotals` / `useBillingByOrg`
 * for cross-org numbers, or pass a specific org id to drill down.
 */
export function useLedger(period: Period, orgScope?: OrgScope) {
  const qs = new URLSearchParams({ period, limit: '200' })
  if (orgScope) qs.set('org_id', orgScope)
  return useQuery({
    queryKey: ['admin', 'ledger', period, orgScope ?? null],
    queryFn: () =>
      apiFetch<{ rows: LedgerRow[]; from_ts: number; to_ts: number }>(
        `/api/admin/ledger?${qs.toString()}`,
      ),
  })
}

export function useRdSpend(period: Period, orgScope?: OrgScope) {
  const qs = new URLSearchParams({ period })
  if (orgScope) qs.set('org_id', orgScope)
  return useQuery({
    queryKey: ['admin', 'rd-spend', period, orgScope ?? null],
    queryFn: () => apiFetch<RdSpend>(`/api/admin/rd-spend?${qs.toString()}`),
  })
}

export function useRunReconciliation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ upserts: number }>('/api/admin/reconcile/run', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'billing'] }),
  })
}

